import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { TextField } from '../components/Form';
import { PasswordField } from '../components/Password';
import { InlineError } from '../components/Feedback';

interface LocationState {
  from?: string;
}

export function SignInPage(): JSX.Element {
  const { signIn, isAuthenticated, signInError } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);

  const from = (location.state as LocationState | null)?.from ?? '/';

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPending(true);
    try {
      await signIn(email.trim(), password);
    } catch {
      // signInError carries the message for display.
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="signin-shell">
      <main className="signin-card">
        <div className="signin-brand">
          <span className="brand-mark" aria-hidden="true">
            iE
          </span>
          <div>
            <h1 className="signin-title">iERP QuickBooks Online Gateway</h1>
            <p className="signin-sub">Sign in to work the invoice queue.</p>
          </div>
        </div>

        <form className="signin-form" onSubmit={onSubmit} noValidate>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            required
            autoComplete="username"
            placeholder="you@company.com"
          />
          <PasswordField
            label="Password"
            value={password}
            onChange={setPassword}
            required
            autoComplete="current-password"
          />

          <InlineError message={signInError} />

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={pending || email.trim() === '' || password === ''}
          >
            {pending ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/forgot-password">Forgot your password?</Link>
          <Link to="/signup">Create an account</Link>
        </div>

        <p className="signin-note">
          Sessions are held in memory only. Reloading this page signs you out.
        </p>
      </main>
    </div>
  );
}
